import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Phone, MapPin, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { directoryCategories } from '../../data/mock';
import Layout from '../../components/layout/Layout';

const DirectoryPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const allCategories = directoryCategories.map(cat => cat.category);

  const filteredCategories = directoryCategories.filter(category => {
    if (activeFilter !== 'all' && category.category !== activeFilter) return false;
    
    if (searchTerm) {
      const hasMatchingCompany = category.companies.some(
        company =>
          company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return hasMatchingCompany;
    }
    return true;
  }).map(category => ({
    ...category,
    companies: category.companies.filter(
      company =>
        !searchTerm ||
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.address.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }));

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <Link to="/acerca-de/historia" className="hover:text-white transition-colors">Acerca de</Link>
              <span>/</span>
              <span className="text-blue-400">Directorio</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Directorio de Socios
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Encuentra a nuestros socios organizados por sector. Más de 180 empresas afiliadas trabajando juntas por el desarrollo de Manzanillo.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar empresa o dirección..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20 focus:border-[#1E40AF] transition-all"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeFilter === 'all'
                    ? 'bg-[#1E40AF] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              {allCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeFilter === cat
                      ? 'bg-[#1E40AF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Directory List */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {filteredCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
              >
                {/* Category Header */}
                <button
                  onClick={() => setExpandedCategory(expandedCategory === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-[#1E40AF]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-[#0A1628] text-lg">
                        Socios {category.category}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {category.companies.length} empresas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-sm font-medium rounded-full">
                      {category.companies.length}
                    </span>
                    {expandedCategory === index ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Companies List */}
                <div className={`transition-all duration-300 overflow-hidden ${
                  expandedCategory === index ? 'max-h-[2000px]' : 'max-h-0'
                }`}>
                  <div className="border-t border-gray-100">
                    <div className="grid md:grid-cols-2 gap-4 p-6">
                      {category.companies.map((company, companyIndex) => (
                        <div
                          key={companyIndex}
                          className="bg-gray-50 rounded-xl p-5 hover:bg-[#1E40AF]/5 transition-colors"
                        >
                          <h4 className="font-semibold text-[#0A1628] mb-3">
                            {company.name}
                          </h4>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2 text-sm text-gray-600">
                              <MapPin className="h-4 w-4 text-[#1E40AF] mt-0.5 flex-shrink-0" />
                              <span>{company.address}</span>
                            </div>
                            <a
                              href={`tel:${company.phone}`}
                              className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#1E40AF] transition-colors"
                            >
                              <Phone className="h-4 w-4 text-[#1E40AF]" />
                              <span>{company.phone}</span>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredCategories.length === 0 && (
              <div className="text-center py-12 bg-white rounded-2xl">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No se encontraron empresas</h3>
                <p className="text-gray-500">Intenta con otros términos de búsqueda</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-4">
            ¿Quieres que tu empresa aparezca en el directorio?
          </h2>
          <p className="text-gray-600 mb-8">
            Afíliate a COPARMEX Manzanillo y forma parte de la comunidad empresarial más importante de la región
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white font-medium rounded-lg hover:bg-[#1E3A8A] transition-all duration-300"
          >
            Conoce cómo afiliarte
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default DirectoryPage;
